import React, { useState } from 'react';
import { PhoneOutlined, FileTextOutlined, CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  FormGroup,
  FormInput,
  FormTextarea,
  TaskTypeToggle,
  TaskTypeButton,
  DateTimeRow,
  SendButton
} from './styled';
import DialerService from '../../services/DialerService';

// Pre-set times in 30-min increments
const TIME_OPTIONS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
];

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Get default time (next half hour)
const getDefaultTime = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const hours = now.getHours();
  
  // Round up to next 30 min
  if (minutes < 30) {
    return `${hours.toString().padStart(2, '0')}:30`;
  } else {
    const nextHour = hours + 1;
    if (nextHour >= 18) return '09:00'; // Next day default
    return `${nextHour.toString().padStart(2, '0')}:00`;
  }
};

const QuickTaskForm = ({ contact }) => {
  const [taskType, setTaskType] = useState('callback'); // 'callback' | 'followup'
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState(getTodayDate());
  const [dueTime, setDueTime] = useState(getDefaultTime());
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const [error, setError] = useState(null);
  
  // Generate default title based on type and contact
  const getDefaultTitle = () => {
    const contactName = contact?.name || 'Contact';
    if (taskType === 'callback') {
      return `Callback: ${contactName}`;
    }
    return `Follow-up: ${contactName}`;
  };
  
  // Handle task type change
  const handleTypeChange = (type) => {
    setTaskType(type);
    // Auto-update title if using default
    if (!title || title.startsWith('Callback:') || title.startsWith('Follow-up:')) {
      const contactName = contact?.name || 'Contact';
      setTitle(type === 'callback' ? `Callback: ${contactName}` : `Follow-up: ${contactName}`);
    }
  };
  
  // Handle create task
  const handleCreate = async () => {
    if (!title.trim()) {
      setError('Please enter a task title');
      return;
    }
    
    setCreating(true);
    setError(null);
    
    try {
      // Combine date and time
      const dueDatetime = new Date(`${dueDate}T${dueTime}:00`);
      
      await DialerService.createTask({
        title: title.trim(),
        description: notes.trim(),
        dueDate: dueDatetime.toISOString(),
        contactId: contact?.id,
        contactPhone: contact?.phone,
        type: taskType,
      });
      
      setCreated(true);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setCreated(false);
        setTitle('');
        setNotes('');
        setDueDate(getTodayDate());
        setDueTime(getDefaultTime());
      }, 3000);
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err.message || 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };
  
  const canCreate = title.trim().length > 0 && !creating && !created;
  
  return (
    <div>
      {/* Task Type Toggle */}
      <FormGroup>
        <label>Task Type</label>
        <TaskTypeToggle>
          <TaskTypeButton
            $active={taskType === 'callback'}
            onClick={() => handleTypeChange('callback')}
          >
            <PhoneOutlined />
            Callback
          </TaskTypeButton>
          <TaskTypeButton
            $active={taskType === 'followup'}
            onClick={() => handleTypeChange('followup')}
          >
            <FileTextOutlined />
            Follow-up
          </TaskTypeButton>
        </TaskTypeToggle>
      </FormGroup>
      
      {/* Task Title */}
      <FormGroup>
        <label>Title</label>
        <FormInput
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={getDefaultTitle()}
          onFocus={() => {
            if (!title) setTitle(getDefaultTitle());
          }}
        />
      </FormGroup>
      
      {/* Date and Time */}
      <DateTimeRow>
        <FormGroup>
          <label>Due Date</label>
          <FormInput
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            min={getTodayDate()}
          />
        </FormGroup>
        <FormGroup>
          <label>Time</label>
          <FormInput
            as="select"
            value={dueTime}
            onChange={(e) => setDueTime(e.target.value)}
          >
            {TIME_OPTIONS.map((time) => (
              <option key={time} value={time}>
                {new Date(`2000-01-01T${time}:00`).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </option>
            ))}
          </FormInput>
        </FormGroup>
      </DateTimeRow>
      
      {/* Notes */}
      <FormGroup>
        <label>Notes (optional)</label>
        <FormTextarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any notes or details..."
          rows={2}
        />
      </FormGroup>
      
      {/* Error Message */}
      {error && (
        <div style={{ 
          color: '#ff4d4f', 
          fontSize: 12, 
          marginBottom: 8,
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
      
      {/* Create Button */}
      <SendButton
        onClick={handleCreate}
        disabled={!canCreate}
        $success={created}
      >
        {creating ? (
          <>
            <LoadingOutlined spin />
            Creating...
          </>
        ) : created ? (
          <>
            <CheckOutlined />
            Task Created!
          </>
        ) : (
          <>
            Create Task
          </>
        )}
      </SendButton>
    </div>
  );
};

export default QuickTaskForm;
